import React, { useEffect, useRef } from "react"

import { Legend } from "./legend"
import { OccupancyToggle } from "./occupancy_toggle"
import { ViewToggle } from "./top_right"
import { OverlayDataProvider, useOverlayData } from "./useOverlayData"
import { useFilterQueryParams } from "../../hooks/filter_query_params"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { FeatureFlags, CAMPUS_FLOW_DISABLED_SCHOOL_IDS } from "@ciq/shared/constants/featureFlags"
import { useAppContext } from "@ciq/shared/hooks/context"

// Extra padding to add around UI elements so map content doesn't sit right against them
const PADDING_OFFSET = 10

/**
 * Calculates map padding based on overlay UI element positions.
 *
 * Purpose: The padding values are passed to Mapbox's fitToBounds() function to ensure
 * that map content (GeoJSON features) is visible and not hidden underneath UI elements
 * like the legend, view toggle, left pane, or right pane. When fitToBounds() is called,
 * it adjusts the map viewport to fit the specified bounds while respecting these padding
 * values, creating a "safe area" where map content will be visible.
 *
 * Calculation:
 * - left: Distance from viewport left edge to left edge of overlay element (accounts for left pane width)
 * - top: Distance from viewport top to bottom of ViewToggle (top-right element)
 * - right: Distance from left edge of Legend to viewport right edge (accounts for legend width)
 * - bottom: Distance from top of Legend to viewport bottom edge (accounts for legend height)
 *
 * The padding is emitted via eventBus "map-padding-changed" and consumed by useMapData,
 * which merges it into the options object when calling fitToBounds().
 */
const getOverlayDimensions = element => {
  // Get the overlay element's position to calculate left padding
  const overlayRect = element.getBoundingClientRect()

  // Find the top-right and bottom-right elements to measure other padding
  const topRight = element.querySelector('.map-button-overlay-top-right')
  const bottomRight = element.querySelector('.map-button-overlay-bottom-right')

  const padding = {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  }

  // Left padding: distance from viewport left edge to overlay left edge
  // This accounts for the left pane width
  padding.left = Math.max(0, overlayRect.left)

  // Right padding: distance from overlay right edge to viewport right edge
  // This accounts for the right pane width
  padding.right = Math.max(0, window.innerWidth - overlayRect.right)

  // Top padding: bottom of ViewToggle (top-right element) + offset
  if (topRight) {
    const rect = topRight.getBoundingClientRect()
    padding.top = Math.max(0, rect.bottom) + PADDING_OFFSET
  }

  // Bottom padding from Legend (bottom-right element) + offset
  if (bottomRight) {
    const rect = bottomRight.getBoundingClientRect()
    // Bottom padding: from bottom of window to top of legend
    // This ensures map content stays ABOVE the legend, not underneath it
    padding.bottom = Math.max(0, window.innerHeight - rect.top) + PADDING_OFFSET
  }

  return padding
}

export function Overlay() {
  return (
    <OverlayDataProvider>
      <OverlayContent />
    </OverlayDataProvider>
  )
}

function OverlayContent() {
  const { setMapPaddingOffset } = useOverlayData()
  const { isHidingOccupancy } = useFilterQueryParams()
  const { isFeatureEnabled } = useAnalyticsContext()
  const { school } = useAppContext()
  const showOccupancyToggle = !CAMPUS_FLOW_DISABLED_SCHOOL_IDS.has(school?.id) && isFeatureEnabled(FeatureFlags.campus_flow)

  const divRef = useRef(null)

  useEffect(() => {
    if (!divRef.current) {
      return
    }

    const updatePadding = () => {
      if (!divRef.current) return

      const padding = getOverlayDimensions(divRef.current)
      setMapPaddingOffset(padding)
    }

    // Set initial dimensions
    updatePadding()

    // Watch for any size changes to the overlay and its children
    const observer = new ResizeObserver(() => {
      updatePadding()
    })

    observer.observe(divRef.current)

    // Also observe the legend and view toggle elements directly
    const topRight = divRef.current.querySelector('.map-button-overlay-top-right')
    const bottomRight = divRef.current.querySelector('.map-button-overlay-bottom-right')

    if (topRight) {
      observer.observe(topRight)
    }

    if (bottomRight) {
      observer.observe(bottomRight)
    }

    // Also recalculate after a short delay to catch any delayed rendering
    const timeoutId = setTimeout(() => {
      updatePadding()
    }, 100)

    return () => {
      observer.disconnect()
      clearTimeout(timeoutId)
    }
  }, [setMapPaddingOffset])

  return (
    <div className="map-button-overlay" ref={divRef}>
      {showOccupancyToggle && (
        <div className="map-button-overlay-top-left">
          <OccupancyToggle />
        </div>
      )}
      <div className="map-button-overlay-top-right">
        <ViewToggle />
      </div>
      <div className="map-button-overlay-bottom-right">
        {!isHidingOccupancy && <Legend />}
      </div>
    </div>
  )
}
